import type { ComputedRef, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'
import { useFiltersDraft } from '@shared/lib/filters'

import { useOrdersStore } from './store'
import { useOrderOptions } from './order-options'

const LESSONS_BOUGHT_MIN = 0
const LESSONS_BOUGHT_MAX = 144

interface OrdersFilters {
  search: string | null
  statuses: string[]
  type: string | null
  from: string | null
  to: string | null
  lessonsFrom: number | null
  lessonsTo: number | null
  promocodeId: number | null
}

interface FiltersOrdersReturn {
  refSearch: WritableComputedRef<string>
  refDateRange: WritableComputedRef<Date[] | null>
  refStatuses: WritableComputedRef<string[]>
  refType: WritableComputedRef<string | undefined>
  refLessonsFrom: WritableComputedRef<string | number>
  refLessonsTo: WritableComputedRef<string | number>
  statusOptions: ComputedRef<AppSelectOption[]>
  typeOptions: ComputedRef<AppSelectOption[]>
  lessonsMin: number
  lessonsMax: number
  isLoading: ComputedRef<boolean>
  clearFilters: () => void
  submitSearch: () => Promise<void>
}

const toQueryCount = (value: number | null): string | undefined =>
  value !== null ? String(value) : undefined

const toCount = (value: string | number): number | null => {
  const parsed = Number(value)

  return value === '' || !Number.isFinite(parsed) ? null : parsed
}

/**
 * The controls only prepare the filters: Search (or Enter in a text field) applies them. The promo code filter has no
 * control — it arrives from the Promo codes page link — so it stays applied until Clear.
 */
export const useFiltersOrders = (): FiltersOrdersReturn => {
  const store = useOrdersStore()
  const dayjs = useDayjs()
  const { statusOptions, typeOptions } = useOrderOptions()

  const { refDraft, isLoading, patchDraft, search, clearFilters } = useFiltersDraft<OrdersFilters>({
    applied: () => ({
      search: store.getFilterSearch,
      statuses: store.getFilterStatuses,
      type: store.getFilterType,
      from: store.getFilterFrom,
      to: store.getFilterTo,
      lessonsFrom: store.getFilterLessonsFrom,
      lessonsTo: store.getFilterLessonsTo,
      promocodeId: store.getFilterPromocodeId,
    }),
    onApply: (filters) => {
      store.setFilterSearch(filters.search?.trim() || null)
      store.setFilterStatuses(filters.statuses)
      store.setFilterType(filters.type)
      store.setFilterFrom(filters.from)
      store.setFilterTo(filters.to)
      store.setFilterLessonsFrom(filters.lessonsFrom)
      store.setFilterLessonsTo(filters.lessonsTo)
    },
    onReset: () => store.resetFilters(),
    toQuery: filters => ({
      q: filters.search || undefined,
      status: filters.statuses.length ? filters.statuses : undefined,
      type: filters.type || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
      lessons_from: toQueryCount(filters.lessonsFrom),
      lessons_to: toQueryCount(filters.lessonsTo),
      promocode_id: filters.promocodeId ? String(filters.promocodeId) : undefined,
    }),
    loading: () => store.isLoading,
  })

  const refSearch = computed<string>({
    get: () => refDraft.value.search ?? '',
    set: (value) => {
      patchDraft({ search: value || null })
    },
  })

  const refDateRange = computed<Date[] | null>({
    get: () => {
      const { from, to } = refDraft.value
      if (!from || !to) return null

      const fromDate = dayjs(from)
      const toDate = dayjs(to)

      return fromDate.isValid() && toDate.isValid() ? [fromDate.toDate(), toDate.toDate()] : null
    },
    set: (value) => {
      const [from, to] = value ?? []

      patchDraft(from && to
        ? { from: dayjs(from).format(DateFormat.Default), to: dayjs(to).format(DateFormat.Default) }
        : { from: null, to: null })
    },
  })

  const refStatuses = computed<string[]>({
    get: () => refDraft.value.statuses,
    set: (value) => {
      patchDraft({ statuses: value })
    },
  })

  const refType = computed<string | undefined>({
    get: () => refDraft.value.type ?? undefined,
    set: (value) => {
      patchDraft({ type: value ?? null })
    },
  })

  const refLessonsFrom = computed<string | number>({
    get: () => refDraft.value.lessonsFrom ?? '',
    set: (value) => {
      patchDraft({ lessonsFrom: toCount(value) })
    },
  })

  const refLessonsTo = computed<string | number>({
    get: () => refDraft.value.lessonsTo ?? '',
    set: (value) => {
      patchDraft({ lessonsTo: toCount(value) })
    },
  })

  return {
    refSearch,
    refDateRange,
    refStatuses,
    refType,
    refLessonsFrom,
    refLessonsTo,
    statusOptions,
    typeOptions,
    lessonsMin: LESSONS_BOUGHT_MIN,
    lessonsMax: LESSONS_BOUGHT_MAX,
    isLoading,
    clearFilters,
    submitSearch: search,
  }
}
